import { EquipmentType, WEIGHT_GRID_TOLERANCE_KG } from '@cf-shared/data-model/equipment';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { SetType } from '@cf-shared/enums';
import { lbsToKg } from '@cf-shared/shared-util';

/** Which wording the hint uses, picked from the kind of equipment that sets the floor. */
export enum EquipmentFloorKind {
  EmptyBar = 'empty_bar',
  LightestFixed = 'lightest_fixed',
  LightestStackPlate = 'lightest_stack_plate',
  LightestSetting = 'lightest_setting',
}

/** Why the field shows the floor, which decides what the hint says. */
export enum EquipmentFloorReason {
  /** a first or rep-test set whose plan target is lighter than the equipment can go (#6151) */
  PlanTarget = 'plan_target',
  /** a deload, drop or rep-max follow-up set that should have been lighter than the equipment can go (#6197) */
  LighterSetBlocked = 'lighter_set_blocked',
}

export interface EquipmentFloorHintInput {
  setType: SetType;
  setState: LogRecordState;
  /** the weight the plan prescribes before it is put on the equipment's grid (`e.targetWeight_kg`) */
  prescribedTarget_kg: number | null | undefined;
  /**
   * the added weight the app worked out for a deload, drop or rep-max follow-up set from an earlier
   * set, before it was put on the equipment's grid. Undefined when the set's weight was not worked out
   */
  derivedWeight_kg?: number | null;
  /** the weight in the set's field right now */
  shownWeight_kg: number | null | undefined;
  /** the lightest weight the equipment can produce, or undefined when it can produce nothing */
  floor_kg: number | undefined;
  equipmentType: EquipmentType | undefined;
}

export interface EquipmentFloorHint {
  /** the weight the app wanted: the plan target, or the weight worked out from an earlier set */
  target_kg: number;
  floor_kg: number;
  kind: EquipmentFloorKind;
  reason: EquipmentFloorReason;
}

// the two set types whose weight `openSet` fills from the plan target, snapped onto the equipment
// with `adjustWeight(e.targetWeight_kg, 'round', …)`.
const SET_TYPES_FILLED_FROM_TARGET: ReadonlySet<SetType> = new Set([SetType.Regular1st, SetType.RepTest]);

// the set types whose weight `openSet` works out from an earlier set and rounds DOWN onto the
// equipment (`setWeightFromNet`). They are meant to be lighter, so landing on the floor means the
// equipment held them up (#6197). `Followup` is not here: it repeats the previous weight by design.
const SET_TYPES_WORKED_OUT_LIGHTER: ReadonlySet<SetType> = new Set([SetType.Deload, SetType.Dropset, SetType.RMTestFollow]);

// the set states in which the field still holds what the app suggested, before the set is finished
const OPEN_SET_STATES: ReadonlySet<LogRecordState> = new Set([LogRecordState.Scheduled, LogRecordState.Incomplete]);

// picks the hint wording for the equipment type, or undefined when that type has no floor wording
function kindForEquipment(equipmentType: EquipmentType | undefined): EquipmentFloorKind | undefined {
  switch (equipmentType) {
    case EquipmentType.Bar: return EquipmentFloorKind.EmptyBar;
    case EquipmentType.Fixed: return EquipmentFloorKind.LightestFixed;
    case EquipmentType.Stack: return EquipmentFloorKind.LightestStackPlate;
    case EquipmentType.Manual:
    case EquipmentType.Plates: return EquipmentFloorKind.LightestSetting;
    default: return undefined;
  }
}

/**
 * Whether the equipment's floor held up a weight the app wanted: the weight is a real added weight
 * (above 0) and lies below the floor by more than lb/kg noise. The page asks this both for the hint
 * and for the deload and drop-set instructions, so the two cannot disagree on the same set (#6197).
 *
 * A weight of 0 never counts: that is a bodyweight exercise with no added weight on loadable
 * equipment, which is normal (#3879). The tolerance is `WEIGHT_GRID_TOLERANCE_KG`, so the same
 * nominal weight stored in lb and kg is not read as two different weights.
 */
export function isBlockedByFloor(wanted_kg: number | null | undefined, floor_kg: number | undefined): boolean {
  if (wanted_kg == null || !Number.isFinite(wanted_kg) || wanted_kg <= 0) {
    return false;
  }
  if (floor_kg == null || !Number.isFinite(floor_kg)) {
    return false;
  }
  return wanted_kg < floor_kg - WEIGHT_GRID_TOLERANCE_KG;
}

/**
 * Whether to tell the user that the weight in the field is the equipment's floor, not the weight the
 * app wanted.
 *
 * #6151: when the plan asks for less than the lightest weight the equipment can produce, rounding the
 * target onto the equipment lands on that lightest weight, and the user sees a number far above
 * their plan with no word about why (#5835: 10 lb planned, 45 lb shown, the empty bar).
 *
 * #6197: a deload, drop or rep-max follow-up set works its weight out from an earlier set and is
 * meant to be lighter. Rounding down cannot go below the floor either, so a user who fails at the
 * empty bar is given a deload set at the empty bar again. For these sets the weight compared is the
 * worked-out one, not the plan target.
 *
 * This only decides; nothing is stored, and the plan's target is left as it is.
 */
export function decideEquipmentFloorHint(input: EquipmentFloorHintInput): EquipmentFloorHint | undefined {
  const { setType, setState, prescribedTarget_kg, derivedWeight_kg, shownWeight_kg, floor_kg, equipmentType } = input;

  // if the set is already finished, the field no longer holds what the app suggested
  if (!OPEN_SET_STATES.has(setState)) {
    return undefined;
  }
  // pick the weight the app wanted for this set type, or stop when the app did not work one out
  let wanted_kg: number | null | undefined;
  let reason: EquipmentFloorReason;
  if (SET_TYPES_FILLED_FROM_TARGET.has(setType)) {
    wanted_kg = prescribedTarget_kg;
    reason = EquipmentFloorReason.PlanTarget;
  } else if (SET_TYPES_WORKED_OUT_LIGHTER.has(setType)) {
    wanted_kg = derivedWeight_kg;
    reason = EquipmentFloorReason.LighterSetBlocked;
  } else {
    return undefined;
  }
  const kind = kindForEquipment(equipmentType);
  // the two null checks repeat what `isBlockedByFloor` checks, so the compiler knows both are numbers below
  if (kind === undefined || wanted_kg == null || floor_kg == null || !isBlockedByFloor(wanted_kg, floor_kg)) {
    return undefined;
  }
  // if the user has already typed a different weight, the field no longer shows the floor
  if (shownWeight_kg == null || Math.abs(shownWeight_kg - floor_kg) > WEIGHT_GRID_TOLERANCE_KG) {
    return undefined;
  }
  return { target_kg: wanted_kg, floor_kg, kind, reason };
}

/**
 * A weight rounded to one decimal in the unit the user sees, returned in kg for `formatMeasure`.
 * The hint's target often comes from a strength estimate, and `formatMeasure` prints three decimals,
 * so without this a target could read "3.704 lb" next to a field that shows whole weights.
 */
export function roundWeightForDisplay_kg(weight_kg: number, unit: string): number {
  const kgPerUnit = unit === 'lb' ? lbsToKg(1) : 1;
  return Math.round((weight_kg / kgPerUnit) * 10) / 10 * kgPerUnit;
}
